/**
 * DataScenarios — DDM-008 / DDM-009 / DDM-010 / DDM-011
 *
 * Admin screen at `/admin/data-scenarios`. Four large card-buttons:
 *
 *   1. Restore current      → POST /api/v1/admin/data-scenarios/restore-current
 *      Loads `baseline-current.json` (the live state at the moment of last snapshot).
 *      Typed-confirm: 'CONFIRM'
 *
 *   2. Load demo dataset    → POST /api/v1/admin/data-scenarios/load
 *      Loads ~860-record fixture (today 181-row skeleton) tagged data_origin='demo'.
 *      No typed gate — just a sign-off modal.
 *
 *   3. Delete demo records  → POST /api/v1/admin/data-scenarios/delete-demo
 *      DELETE WHERE data_origin='demo'. Real records untouched.
 *      No typed gate — surgical.
 *
 *   4. Reset to delivery    → POST /api/v1/admin/data-scenarios/reset-clean
 *      Wipes baseline+demo (and optionally real); seeds clean-delivery.json.
 *      Typed-confirm: 'RESET' (or 'DELETE EVERYTHING' if wipeReal=true).
 *
 * Every action is audit-logged as DEMO_DATA_SCENARIO_CHANGED.
 * Inspector Mode reads `/api/v1/admin/data-scenarios/recent-switches`
 * and shows a banner when the current scenario isn't 'baseline-current'.
 */

import { useCallback, useEffect, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { Button, Card, CardBody, CardHeader } from '@edms/design-system';
import styles from './DataScenarios.module.css';

type Scenario = 'full-demo' | 'baseline-current' | 'clean-delivery';

interface ScenarioResult {
  ok: boolean;
  scenario?: Scenario;
  counts: Record<string, number>;
  totalRows?: number;
  totalDeleted?: number;
  mockMode: boolean;
}

interface ScenarioSwitch {
  audit_id: string;
  event_kind: string;
  action: string;
  scenario?: Scenario;
  total_rows: number;
  recorded_at_utc: string;
  actor_user_id: string | null;
  mock_mode?: boolean;
}

interface CurrentScenarioState {
  scenario: string;
  loadedAt: string | null;
  totalRows: number;
  mockMode?: boolean;
}

interface StatusResponse {
  mockMode: boolean;
  summary: { real: number | string; demo: number; baseline: number };
  perTable: Array<{ tableName: string; real: number; demo: number; baseline: number }>;
  scenarioHistory: ScenarioSwitch[];
}

export function DataScenarios() {
  const [current, setCurrent] = useState<CurrentScenarioState | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [recent, setRecent] = useState<ScenarioSwitch[]>([]);

  const [busy, setBusy] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<null | 'load-demo' | 'delete-demo' | 'restore' | 'reset' | 'reset-wipe-real'>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; message: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const h = buildHeaders();
      const [c, s, r] = await Promise.all([
        fetch(apiUrl('/api/v1/admin/data-scenarios/current'),         { headers: h }).then((x) => x.json()),
        fetch(apiUrl('/api/v1/admin/data-scenarios/status'),          { headers: h }).then((x) => x.json()),
        fetch(apiUrl('/api/v1/admin/data-scenarios/recent-switches?limit=5'), { headers: h }).then((x) => x.json()),
      ]);
      setCurrent(c);
      setStatus(s);
      setRecent(Array.isArray(r) ? r : []);
    } catch (err) {
      setFeedback({ kind: 'err', message: `refresh failed: ${(err as Error).message}` });
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const callAction = async (path: string, body: Record<string, unknown>, label: string) => {
    setBusy(label);
    setFeedback(null);
    try {
      const res = await fetch(apiUrl(`/api/v1/admin/data-scenarios/${path}`), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as ScenarioResult & { error?: string };
      if (!res.ok) {
        setFeedback({ kind: 'err', message: data.error || `${label} failed (${res.status})` });
      } else {
        const total = data.totalRows ?? data.totalDeleted ?? 0;
        const mock = data.mockMode ? ' (mock mode — DB not mutated)' : '';
        setFeedback({ kind: 'ok', message: `${label} OK: ${total} rows affected${mock}` });
      }
    } catch (err) {
      setFeedback({ kind: 'err', message: `${label} failed: ${(err as Error).message}` });
    } finally {
      setBusy(null);
      setOpenModal(null);
      setConfirmInput('');
      await refresh();
    }
  };

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Data scenarios</h1>
          <p className={styles.subtitle}>
            Swap between live data and curated demo fixtures for the Spectropharma tenant.
            Every switch is audit-logged as <code>DEMO_DATA_SCENARIO_CHANGED</code> and visible to inspectors.
          </p>
        </div>
        <div className={styles.currentBadge}>
          <span className={styles.currentLabel}>Active scenario</span>
          <span className={styles.currentValue}>{current?.scenario || 'unknown'}</span>
          {current?.loadedAt && (
            <span className={styles.currentMeta}>
              loaded {new Date(current.loadedAt).toLocaleString()} · {current.totalRows} rows
            </span>
          )}
          {current?.mockMode && <span className={styles.mockMode}>mock mode</span>}
        </div>
      </header>

      {feedback && (
        <div className={feedback.kind === 'ok' ? styles.feedbackOk : styles.feedbackErr} role="status">
          {feedback.message}
        </div>
      )}

      {status?.mockMode && (
        <div className={styles.mockBanner} role="note">
          Mock-api mode — counts are read from fixtures; no Postgres mutation occurs.
          To run against real Postgres, use <code>node scripts/load-scenario.mjs</code>.
        </div>
      )}

      <section className={styles.cards} aria-label="data scenario actions">
        <ScenarioCard
          color="orange"
          title="Load demo dataset"
          description="Adds ~180 rows tagged data_origin='demo' across 21 business tables. Exercises every AI surface."
          buttonLabel="Load demo"
          busy={busy === 'load-demo'}
          onClick={() => setOpenModal('load-demo')}
        />
        <ScenarioCard
          color="red"
          title="Delete demo records"
          description="DELETE WHERE data_origin='demo' across all business tables. Real + baseline rows untouched."
          buttonLabel="Delete demo only"
          busy={busy === 'delete-demo'}
          onClick={() => setOpenModal('delete-demo')}
        />
        <ScenarioCard
          color="blue"
          title="Restore current"
          description="Reload baseline-current.json — the snapshot of live data taken at V24 cutover. Typed CONFIRM gate."
          buttonLabel="Restore current"
          busy={busy === 'restore'}
          onClick={() => setOpenModal('restore')}
        />
        <ScenarioCard
          color="deep-red"
          title="Reset to delivery state"
          description="Wipes baseline + demo rows, then seeds clean-delivery.json. Typed RESET gate. With --wipe-real, also wipes real data."
          buttonLabel="Reset"
          busy={busy === 'reset'}
          onClick={() => setOpenModal('reset')}
        />
      </section>

      <section className={styles.history} aria-label="recent scenario switches">
        <h2 className={styles.sectionTitle}>Recent scenario switches</h2>
        {recent.length === 0 ? (
          <p className={styles.empty}>No scenario switches recorded yet.</p>
        ) : (
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>Recorded</th>
                <th>Action</th>
                <th>Scenario</th>
                <th>Rows</th>
                <th>Actor</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.audit_id}>
                  <td>{new Date(r.recorded_at_utc).toLocaleString()}</td>
                  <td>{r.action}</td>
                  <td>{r.scenario || '—'}</td>
                  <td>{r.total_rows}</td>
                  <td>{r.actor_user_id || '—'}</td>
                  <td>{r.mock_mode ? 'mock' : 'real'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {status && (
        <section className={styles.statusBlock} aria-label="origin counts">
          <h2 className={styles.sectionTitle}>Origin counts</h2>
          <p>
            real {String(status.summary.real)} · demo {status.summary.demo} · baseline {status.summary.baseline}
          </p>
        </section>
      )}

      {/* ----- Confirmation modals ----- */}
      {openModal === 'load-demo' && (
        <ConfirmModal
          title="Load demo dataset"
          body="This inserts ~180 demo rows. Real + baseline rows are not affected. Existing demo rows are replaced."
          confirmLabel="Load demo"
          tone="warn"
          onCancel={() => setOpenModal(null)}
          onConfirm={() => callAction('load', { scenario: 'full-demo' }, 'load-demo')}
        />
      )}

      {openModal === 'delete-demo' && (
        <ConfirmModal
          title="Delete demo records"
          body="DELETE WHERE data_origin='demo' across all business tables. Surgical — real + baseline rows untouched."
          confirmLabel="Delete demo only"
          tone="warn"
          onCancel={() => setOpenModal(null)}
          onConfirm={() => callAction('delete-demo', {}, 'delete-demo')}
        />
      )}

      {openModal === 'restore' && (
        <TypedConfirmModal
          title="Restore current snapshot"
          body="Reloads baseline-current.json — the snapshot of live data taken at V24 cutover. Rows tagged 'baseline' in conflicting tables will be replaced."
          expectedToken="CONFIRM"
          confirmInput={confirmInput}
          setConfirmInput={setConfirmInput}
          tone="warn"
          onCancel={() => setOpenModal(null)}
          onConfirm={() => callAction('restore-current', { confirmation: 'CONFIRM' }, 'restore-current')}
        />
      )}

      {openModal === 'reset' && (
        <TypedConfirmModal
          title="Reset to delivery state"
          body="Wipes ALL baseline + demo rows, then seeds the clean-delivery.json fixture. Real rows are preserved. Use the wipe-real variant only when handing the tenant over to a fresh customer."
          expectedToken="RESET"
          confirmInput={confirmInput}
          setConfirmInput={setConfirmInput}
          tone="danger"
          secondaryButton={{
            label: 'Switch to wipe-real',
            onClick: () => { setOpenModal('reset-wipe-real'); setConfirmInput(''); },
          }}
          onCancel={() => setOpenModal(null)}
          onConfirm={() => callAction('reset-clean', { confirmation: 'RESET' }, 'reset-clean')}
        />
      )}

      {openModal === 'reset-wipe-real' && (
        <TypedConfirmModal
          title="DANGER — wipe all data including real"
          body="This deletes EVERY data_origin row (demo, baseline, AND real) across all business tables. Use only for green-field customer handover. Audit chain is preserved; deleted rows cannot be recovered."
          expectedToken="DELETE EVERYTHING"
          confirmInput={confirmInput}
          setConfirmInput={setConfirmInput}
          tone="danger"
          onCancel={() => setOpenModal(null)}
          onConfirm={() => callAction('reset-clean', { confirmation: 'DELETE EVERYTHING', wipeReal: true }, 'reset-clean-WIPE-REAL')}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Sub-components
// ---------------------------------------------------------------------------
interface ScenarioCardProps {
  color: 'blue' | 'orange' | 'red' | 'deep-red';
  title: string;
  description: string;
  buttonLabel: string;
  busy: boolean;
  onClick: () => void;
}
function ScenarioCard({ color, title, description, buttonLabel, busy, onClick }: ScenarioCardProps) {
  return (
    <Card variant="elevated" className={`${styles.card} ${styles[`card_${color}`]}`}>
      <CardHeader>
        <h3 className={styles.cardTitle}>{title}</h3>
      </CardHeader>
      <CardBody>
        <p className={styles.cardDescription}>{description}</p>
        <Button variant="primary" onClick={onClick} disabled={busy} className={styles.cardButton}>
          {busy ? 'Working…' : buttonLabel}
        </Button>
      </CardBody>
    </Card>
  );
}

interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  tone: 'warn' | 'danger';
  onCancel: () => void;
  onConfirm: () => void;
}
function ConfirmModal({ title, body, confirmLabel, tone, onCancel, onConfirm }: ConfirmModalProps) {
  return (
    <ModalShell onCancel={onCancel} title={title}>
      <p>{body}</p>
      <div className={styles.modalActions}>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          className={tone === 'danger' ? styles.dangerBtn : styles.warnBtn}
        >
          {confirmLabel}
        </Button>
      </div>
    </ModalShell>
  );
}

interface TypedConfirmProps {
  title: string;
  body: string;
  expectedToken: string;
  confirmInput: string;
  setConfirmInput: (v: string) => void;
  tone: 'warn' | 'danger';
  onCancel: () => void;
  onConfirm: () => void;
  secondaryButton?: { label: string; onClick: () => void };
}
function TypedConfirmModal({
  title, body, expectedToken, confirmInput, setConfirmInput,
  tone, onCancel, onConfirm, secondaryButton,
}: TypedConfirmProps) {
  const matches = confirmInput === expectedToken;
  return (
    <ModalShell onCancel={onCancel} title={title}>
      <p>{body}</p>
      <p className={styles.typedHint}>
        Type <code>{expectedToken}</code> to confirm:
      </p>
      <input
        type="text"
        value={confirmInput}
        onChange={(e) => setConfirmInput(e.target.value)}
        className={styles.typedInput}
        autoFocus
        aria-label="confirmation token"
      />
      <div className={styles.modalActions}>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        {secondaryButton && (
          <Button variant="secondary" onClick={secondaryButton.onClick}>{secondaryButton.label}</Button>
        )}
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={!matches}
          className={tone === 'danger' ? styles.dangerBtn : styles.warnBtn}
        >
          {matches ? `Proceed — ${title}` : `Type ${expectedToken} to enable`}
        </Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onCancel, children }: { title: string; onCancel: () => void; children: React.ReactNode }) {
  return (
    <div
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="data-scenarios-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className={styles.modal}>
        <h2 id="data-scenarios-modal-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}
